import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Receipt, Plus, Trash2, FileDown, Check, X, Camera, ArrowLeft, Send, Loader2, ClipboardCheck, CreditCard } from 'lucide-react';
import VisaStatements from './expenses/VisaStatements';
import MyVisaCharges from './expenses/MyVisaCharges';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/expenses`;

const STATUS_STYLES = {
  draft: 'bg-slate-200 text-slate-700 border-slate-500/30',
  submitted: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

const fmt = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Expenses = () => {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [isApprover, setIsApprover] = useState(false);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'mine');
  const [pending, setPending] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visa, setVisa] = useState({ is_admin: false, has_card_activity: false });

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reports`);
      setReports(r.data.reports || []);
      setPendingCount(r.data.pending_approval || 0);
      setIsApprover(!!r.data.is_approver);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPending = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/reports/pending`);
      setPending(r.data.reports || []);
    } catch (e) { /* not approver */ }
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);
  useEffect(() => { if (tab === 'approvals') loadPending(); }, [tab, loadPending]);
  useEffect(() => { axios.get(`${API}/visa/access`).then(r => setVisa(r.data)).catch(() => {}); }, []);

  const createReport = async () => {
    try {
      const r = await axios.post(`${API}/reports`, {});
      setSelectedId(r.data.id);
      loadReports();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create report');
    }
  };

  if (selectedId) {
    return <ReportDetail reportId={selectedId} onBack={() => { setSelectedId(null); loadReports(); if (tab === 'approvals') loadPending(); }} />;
  }

  const list = tab === 'approvals' ? pending : reports;

  return (
    <div className="animate-fade-in" data-testid="expenses-page">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-1 flex items-center gap-2"><Receipt className="w-7 h-7 text-blue-700" /> Expenses</h1>
          <p className="text-slate-500 text-sm">Snap a receipt — we read it, you just add the Job #.</p>
        </div>
        <Button data-testid="new-expense-report-btn" onClick={createReport} className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" /> New Expense Report
        </Button>
      </div>

      {(isApprover || visa.is_admin || visa.has_card_activity) && (
        <div className="flex flex-wrap gap-1 bg-white p-1 rounded-xl border border-slate-200 mb-6 w-fit">
          <button data-testid="exp-tab-mine" onClick={() => setTab('mine')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'mine' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-white'}`}>
            My Reports
          </button>
          {isApprover && (
            <button data-testid="exp-tab-approvals" onClick={() => setTab('approvals')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'approvals' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-white'}`}>
              <ClipboardCheck className="w-4 h-4" /> Approvals
              {pendingCount > 0 && <span className="ml-1 text-xs px-1.5 py-0.5 rounded-full bg-amber-500 text-white">{pendingCount}</span>}
            </button>
          )}
          {visa.has_card_activity && (
            <button data-testid="exp-tab-myvisa" onClick={() => setTab('myvisa')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'myvisa' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-white'}`}>
              <CreditCard className="w-4 h-4" /> My Visa Charges
            </button>
          )}
          {visa.is_admin && (
            <button data-testid="exp-tab-visa" onClick={() => setTab('visa')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${tab === 'visa' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-white'}`}>
              <CreditCard className="w-4 h-4" /> Visa Statement
            </button>
          )}
        </div>
      )}

      {tab === 'visa' && visa.is_admin ? (
        <VisaStatements />
      ) : tab === 'myvisa' ? (
        <MyVisaCharges onOpenReport={(id) => setSelectedId(id)} />
      ) : loading ? (
        <p className="text-slate-500 text-center py-10">Loading…</p>
      ) : list.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center" data-testid="expenses-empty">
          <Receipt className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-500">{tab === 'approvals' ? 'No reports waiting for approval.' : 'No expense reports yet. Create one to get started.'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map(r => {
            const grand = (r.totals?.personal?.invoice_total || 0) + (r.totals?.company_card?.invoice_total || 0);
            return (
              <button key={r.id} data-testid={`expense-report-${r.id}`} onClick={() => setSelectedId(r.id)}
                className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-blue-300 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-slate-900 font-semibold text-sm">{r.title}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[r.status] || STATUS_STYLES.draft}`}>{r.status}</span>
                </div>
                {tab === 'approvals' && <p className="text-xs text-slate-500 mb-1">{r.employee_name}</p>}
                <p className="text-2xl font-bold text-slate-900">{fmt(grand)}</p>
                <p className="text-xs text-slate-500 mt-1">{r.line_count ?? (r.lines?.length || 0)} receipt(s) · {new Date(r.created_at).toLocaleDateString()}</p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const PAYMENT_LABEL = { personal: 'Personal Cash & Credit', company_card: 'Company Card' };

const ReportDetail = ({ reportId, onBack }) => {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState('');
  const [busy, setBusy] = useState(false);

  const token = localStorage.getItem('token');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/reports/${reportId}`);
      setReport(r.data);
    } catch (e) {
      toast.error('Failed to load report');
      onBack();
    }
  }, [reportId, onBack]);

  useEffect(() => { load(); }, [load]);

  const isOwner = report && report.employee_id === user?.id;
  const editable = report && report.status !== 'approved' && (isOwner || report.status === 'submitted');
  const canApprove = report && report.status === 'submitted' && !isOwner;

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setScanning(true);
    let ok = 0;
    for (let i = 0; i < files.length; i++) {
      setScanProgress(`Reading receipt ${i + 1} of ${files.length}…`);
      const fd = new FormData();
      fd.append('file', files[i]);
      try {
        await axios.post(`${API}/reports/${reportId}/scan-receipt`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        ok++;
      } catch (err) {
        toast.error(`Receipt ${i + 1}: ${err.response?.data?.detail || 'failed to read'}`);
      }
    }
    setScanning(false);
    setScanProgress('');
    if (ok) toast.success(`${ok} receipt(s) added — set the Job # for each`);
    load();
  };

  const updateLine = async (lineId, patch) => {
    setReport(prev => ({ ...prev, lines: prev.lines.map(l => l.line_id === lineId ? { ...l, ...patch } : l) }));
  };

  const saveLine = async (lineId, patch) => {
    try {
      await axios.put(`${API}/reports/${reportId}/lines/${lineId}`, patch);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    }
  };

  const deleteLine = async (lineId) => {
    if (!window.confirm('Remove this receipt line?')) return;
    try {
      await axios.delete(`${API}/reports/${reportId}/lines/${lineId}`);
      load();
    } catch (e) { toast.error('Failed to remove'); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/reports/${reportId}/submit`);
      toast.success('Submitted to Jeff H for approval');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/reports/${reportId}/approve`, {});
      toast.success('Report approved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    const reason = window.prompt('Reason for returning this report (optional):') ?? null;
    if (reason === null) return;
    setBusy(true);
    try {
      await axios.post(`${API}/reports/${reportId}/reject`, { reason });
      toast.success('Report returned to employee');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const deleteReport = async () => {
    if (!window.confirm('Delete this entire report?')) return;
    try {
      await axios.delete(`${API}/reports/${reportId}`);
      toast.success('Report deleted');
      onBack();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (!report) return <p className="text-slate-500 text-center py-10">Loading…</p>;

  const sections = ['personal', 'company_card'];

  return (
    <div className="animate-fade-in" data-testid="expense-report-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-slate-500 hover:text-slate-900 text-sm mb-4" data-testid="expense-back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to Expenses
      </button>

      <div className="flex justify-between items-start mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">{report.title}
            <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[report.status]}`}>{report.status}</span>
          </h1>
          <p className="text-slate-500 text-sm">{report.employee_name}</p>
          {report.status === 'rejected' && report.rejection_reason && (
            <p className="text-red-700 text-sm mt-1">Returned: {report.rejection_reason}</p>
          )}
          {report.status === 'approved' && (
            <p className="text-emerald-700 text-sm mt-1">Approved by {report.approved_by_name}</p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={`${API}/reports/${reportId}/pdf?token=${token}`} target="_blank" rel="noreferrer"
            data-testid="expense-pdf-btn"
            className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium bg-slate-100 hover:bg-slate-200 text-slate-900">
            <FileDown className="w-4 h-4 mr-2" /> View / Print PDF
          </a>
          {editable && isOwner && (report.status === 'draft' || report.status === 'rejected') && (
            <Button data-testid="expense-submit-btn" onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Send className="w-4 h-4 mr-2" /> Submit for Approval
            </Button>
          )}
          {canApprove && (
            <>
              <Button data-testid="expense-approve-btn" onClick={approve} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Check className="w-4 h-4 mr-2" /> Approve
              </Button>
              <Button data-testid="expense-reject-btn" onClick={reject} disabled={busy} variant="outline" className="text-red-700 border-red-200 hover:bg-red-50">
                <X className="w-4 h-4 mr-2" /> Return
              </Button>
            </>
          )}
          {isOwner && report.status !== 'approved' && (
            <Button data-testid="expense-delete-btn" onClick={deleteReport} variant="outline" className="text-red-700 border-red-200 hover:bg-red-50">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {editable && isOwner && (
        <label data-testid="receipt-upload-label"
          className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-blue-200 bg-blue-50 hover:bg-blue-50 rounded-xl p-6 mb-6 cursor-pointer transition-colors">
          {scanning ? (
            <><Loader2 className="w-7 h-7 text-blue-700 animate-spin" /><span className="text-blue-700 text-sm">{scanProgress}</span></>
          ) : (
            <><Camera className="w-7 h-7 text-blue-700" /><span className="text-blue-700 text-sm font-medium">Take a photo or upload receipt(s)</span>
              <span className="text-slate-500 text-xs">You can add several at once</span></>
          )}
          <input type="file" accept="image/*" capture="environment" multiple className="hidden" disabled={scanning}
            onChange={handleFiles} data-testid="receipt-file-input" />
        </label>
      )}

      {report.lines.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">
          No receipts yet.
        </div>
      ) : (
        sections.map(section => {
          const lines = report.lines.filter(l => l.payment_method === section);
          const t = report.totals?.[section] || {};
          if (lines.length === 0) return null;
          return (
            <div key={section} className="mb-6">
              <div className="flex items-center justify-between bg-blue-100 border border-blue-200 rounded-t-lg px-4 py-2">
                <h3 className="text-slate-900 font-semibold text-sm">{PAYMENT_LABEL[section]}</h3>
                <span className="text-xs text-slate-700">Total {fmt(t.invoice_total)} · HST {fmt(t.hst)}{(t.other_tax || 0) > 0 ? ` · Other ${fmt(t.other_tax)}` : ''}</span>
              </div>
              <div className="space-y-3 bg-white border border-t-0 border-slate-200 rounded-b-lg p-3">
                {lines.map(l => (
                  <LineCard key={l.line_id} line={l} reportId={reportId} token={token} editable={editable}
                    onChange={updateLine} onSave={saveLine} onDelete={deleteLine} onReload={load} />
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

const LineCard = ({ line, reportId, token, editable, onChange, onSave, onDelete, onReload }) => {
  const inputCls = 'w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-slate-900 text-sm outline-none focus:border-blue-500';
  const [showImg, setShowImg] = useState(false);
  const [slipBusy, setSlipBusy] = useState(false);
  const jobMissing = !(line.job_number || '').trim();
  const isTip = !!line.tip_for_line_id;

  const addCardSlip = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setSlipBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/reports/${reportId}/lines/${line.line_id}/card-slip`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Card charged ${fmt(r.data.card_total)} − receipt ${fmt(r.data.receipt_total)} → Tip ${fmt(r.data.tip)} added`);
      onReload?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not read the card slip');
    }
    setSlipBusy(false);
  };

  return (
    <div data-testid={`expense-line-${line.line_id}`} className={`rounded-lg border p-3 ${jobMissing ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}>
      <div className="flex gap-3">
        {line.receipt_stored && (
          <img src={`${API}/reports/${reportId}/receipt/${line.line_id}?token=${token}`} alt="receipt" onClick={() => setShowImg(true)}
            className="w-20 h-24 object-cover rounded bg-white cursor-pointer flex-shrink-0" data-testid={`receipt-thumb-${line.line_id}`} />
        )}
        <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-2">
          <div className="col-span-2 md:col-span-1">
            <label className="text-[11px] text-slate-500">Job # {jobMissing && <span className="text-amber-700">*required</span>}</label>
            <input className={inputCls + (jobMissing ? ' border-amber-500' : '')} value={line.job_number || ''} disabled={!editable}
              data-testid={`line-job-${line.line_id}`}
              onChange={e => onChange(line.line_id, { job_number: e.target.value })}
              onBlur={e => onSave(line.line_id, { job_number: e.target.value })} placeholder="e.g. 24-2053" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Date</label>
            <input type="date" className={inputCls} value={line.date ? line.date.slice(0, 10) : ''} disabled={!editable}
              onChange={e => onChange(line.line_id, { date: e.target.value })}
              onBlur={e => onSave(line.line_id, { date: e.target.value })} />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Supplier</label>
            <input className={inputCls} value={line.supplier || ''} disabled={!editable}
              onChange={e => onChange(line.line_id, { supplier: e.target.value })}
              onBlur={e => onSave(line.line_id, { supplier: e.target.value })} />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Payment</label>
            <select className={inputCls} value={line.payment_method} disabled={!editable}
              data-testid={`line-payment-${line.line_id}`}
              onChange={e => { onChange(line.line_id, { payment_method: e.target.value }); onSave(line.line_id, { payment_method: e.target.value }); }}>
              <option value="personal">Personal Cash & Credit</option>
              <option value="company_card">Company Card</option>
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-[11px] text-slate-500">Description</label>
            <input className={inputCls} value={line.description || ''} disabled={!editable}
              onChange={e => onChange(line.line_id, { description: e.target.value })}
              onBlur={e => onSave(line.line_id, { description: e.target.value })} />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Expense (pre-tax)</label>
            <input type="number" step="0.01" className={inputCls} value={line.expense_amount ?? ''} disabled={!editable}
              onChange={e => onChange(line.line_id, { expense_amount: e.target.value })}
              onBlur={e => onSave(line.line_id, { expense_amount: e.target.value })} />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">HST</label>
            <input type="number" step="0.01" className={inputCls} value={line.hst ?? ''} disabled={!editable}
              onChange={e => onChange(line.line_id, { hst: e.target.value })}
              onBlur={e => onSave(line.line_id, { hst: e.target.value })} />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Other Taxes (GST/PST/QST)</label>
            <input type="number" step="0.01" className={inputCls} value={line.other_tax ?? ''} disabled={!editable}
              data-testid={`line-othertax-${line.line_id}`}
              onChange={e => onChange(line.line_id, { other_tax: e.target.value })}
              onBlur={e => onSave(line.line_id, { other_tax: e.target.value })} placeholder="0.00" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Other Tax type</label>
            <input className={inputCls} value={line.other_tax_label || ''} disabled={!editable}
              data-testid={`line-othertaxlabel-${line.line_id}`}
              onChange={e => onChange(line.line_id, { other_tax_label: e.target.value })}
              onBlur={e => onSave(line.line_id, { other_tax_label: e.target.value })} placeholder="e.g. GST 5% + PST 7%" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Invoice Total</label>
            <input type="number" step="0.01" className={inputCls} value={line.invoice_total ?? ''} disabled={!editable}
              onChange={e => onChange(line.line_id, { invoice_total: e.target.value })}
              onBlur={e => onSave(line.line_id, { invoice_total: e.target.value })} />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Account Code</label>
            <input className={inputCls} value={line.account_code || ''} disabled={!editable}
              onChange={e => onChange(line.line_id, { account_code: e.target.value })}
              onBlur={e => onSave(line.line_id, { account_code: e.target.value })} placeholder="(blank)" />
          </div>
        </div>
        {editable && (
          <button onClick={() => onDelete(line.line_id)} data-testid={`line-delete-${line.line_id}`}
            className="text-red-600 hover:text-red-700 flex-shrink-0"><Trash2 className="w-4 h-4" /></button>
        )}
      </div>
      {line.confidence === 'low' && <p className="text-[11px] text-amber-700 mt-1">Low confidence — please double-check the amounts.</p>}
      {line.lost_receipt && (
        <p className="text-[11px] text-amber-700 mt-1 font-semibold" data-testid={`lost-receipt-note-${line.line_id}`}>LOST RECEIPT — from the Visa statement, full amount expensed, HST $0.</p>
      )}
      {isTip && (
        <p className="text-[11px] text-emerald-700 mt-1" data-testid={`tip-note-${line.line_id}`}>
          Tip from card slip{line.card ? ` (${line.card}${line.card_last4 ? ` …${line.card_last4}` : ''})` : ''}: card charged {fmt(line.card_total)} — follows the dinner receipt's job & date.
        </p>
      )}
      {!isTip && line.card_slip_line_id && (
        <p className="text-[11px] text-emerald-700 mt-1" data-testid={`slip-done-${line.line_id}`}>Card slip added — charged {fmt(line.card_total)}, tip costed as its own line.</p>
      )}
      {!isTip && !line.card_slip_line_id && editable && (
        <label className={`inline-flex items-center gap-1 mt-2 text-[11px] px-2 py-1 rounded border border-slate-300 text-slate-700 hover:text-slate-900 hover:border-slate-400 cursor-pointer ${slipBusy ? 'opacity-60 pointer-events-none' : ''}`}
          title="Restaurant bill? Add the little card terminal slip — the difference becomes a Tip line on the same job" data-testid={`add-card-slip-${line.line_id}`}>
          <CreditCard className="w-3 h-3" />{slipBusy ? 'Reading card slip…' : 'Add card slip → Tip'}
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={addCardSlip} disabled={slipBusy} data-testid={`card-slip-input-${line.line_id}`} />
        </label>
      )}

      {showImg && line.receipt_stored && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowImg(false)}>
          <img src={`${API}/reports/${reportId}/receipt/${line.line_id}?token=${token}`} alt="receipt" className="max-h-[90vh] max-w-[90vw] rounded-lg" />
        </div>
      )}
    </div>
  );
};

export default Expenses;
